import type { AudioProvider, Track, PlaybackState, PlaybackListener } from "../types/audio";
import { ITunesProvider } from "./iTunesProvider";
import { SelfHostedProvider } from "./selfHostedProvider";
import { detectOnsets, getClipEndForStage, type OnsetResult } from "./onsetDetector";
import { detectBeats, getClipEndForBeatStage, type BeatResult } from "./beatDetector";

export type Stage = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * How the progressive reveal works:
 * - "notes": each stage adds one detected note/onset
 * - "beats": each stage adds one detected beat (rhythmic pulse)
 * - "seconds": classic Heardle-style, each stage = N seconds (fallback)
 */
export type RevealMode = "notes" | "beats" | "seconds";

export interface GameTrack {
  track: Track;
  startSec: number;
  /** Detected onsets (populated after load when mode is "notes") */
  onsets?: OnsetResult;
  /** Detected beats (populated after load when mode is "beats") */
  beatData?: BeatResult;
}

export type StartMode =
  | { kind: "fixed"; startSec: number }
  | { kind: "random" }
  | { kind: "random-range"; minSec: number; maxSec: number }
  | { kind: "random-avoid"; excludeSecs: number[]; minDistance: number };

const STAGE_DURATIONS = [1, 2, 3, 4, 5, 6] as const;

export class GameAudioEngine {
  private itunes: ITunesProvider;
  private selfHosted: SelfHostedProvider | null;
  private activeProvider: AudioProvider | null = null;
  private currentTrack: GameTrack | null = null;
  private currentStage: Stage = 1;
  private revealMode: RevealMode = "notes";
  private listeners: Set<PlaybackListener> = new Set();
  private unsubProvider: (() => void) | null = null;

  constructor(options?: { selfHostedBaseUrl?: string; revealMode?: RevealMode }) {
    this.itunes = new ITunesProvider();
    this.selfHosted = options?.selfHostedBaseUrl
      ? new SelfHostedProvider(options.selfHostedBaseUrl)
      : null;
    this.revealMode = options?.revealMode ?? "notes";
  }

  setRevealMode(mode: RevealMode): void {
    this.revealMode = mode;
  }

  getRevealMode(): RevealMode {
    return this.revealMode;
  }

  async loadTrack(
    artist: string,
    title: string,
    start?: StartMode,
    fallback?: Track,
    itunesId?: string
  ): Promise<GameTrack> {
    // Prefer direct lookup by iTunes ID (deterministic), fall back to text search
    let track = itunesId ? await this.itunes.lookupById(itunesId) : null;
    if (!track) track = await this.itunes.search(artist, title);
    let provider: AudioProvider = this.itunes;

    if (!track && fallback && this.selfHosted) {
      track = fallback;
      provider = this.selfHosted;
    }

    if (!track) {
      throw new Error(`Track not found: "${title}" by ${artist}`);
    }

    this.unsubProvider?.();
    this.activeProvider = provider;
    await provider.load(track);

    this.unsubProvider = provider.onStateChange((state) => {
      for (const listener of this.listeners) listener(state);
    });

    const duration = provider.getState().duration;

    // Run audio analysis based on reveal mode
    // We analyze for both modes so switching is instant
    const audioBuffer = provider.getAudioBuffer();
    let onsets: OnsetResult | undefined;
    let beatData: BeatResult | undefined;

    if (audioBuffer) {
      onsets = detectOnsets(audioBuffer);
      beatData = detectBeats(audioBuffer);
    }

    // Resolve start position
    const maxStageClip = STAGE_DURATIONS[STAGE_DURATIONS.length - 1];
    const safeMax = Math.max(0, duration - maxStageClip);
    const startSec = this.resolveStartSec(start, safeMax);

    this.currentTrack = { track, startSec, onsets, beatData };
    this.currentStage = 1;

    return this.currentTrack;
  }

  /** Play the current stage's clip based on the active reveal mode. */
  async playCurrentStage(): Promise<void> {
    if (!this.activeProvider || !this.currentTrack) {
      throw new Error("No track loaded.");
    }

    const { startSec } = this.currentTrack;
    const totalDuration = this.activeProvider.getState().duration;
    const clipEnd = this.getClipEnd(this.currentStage, startSec, totalDuration);
    const clipDuration = clipEnd - startSec;

    await this.activeProvider.playClip(startSec, clipDuration);
  }

  /** Get the end timestamp for a given stage based on current reveal mode. */
  private getClipEnd(stage: number, startSec: number, totalDuration: number): number {
    switch (this.revealMode) {
      case "notes": {
        const onsets = this.currentTrack?.onsets?.onsets;
        if (onsets?.length) {
          return getClipEndForStage(onsets, stage, startSec, totalDuration);
        }
        // Fallback to seconds if no onsets detected
        return Math.min(startSec + stage, totalDuration);
      }
      case "beats": {
        const beats = this.currentTrack?.beatData?.beats;
        if (beats?.length) {
          return getClipEndForBeatStage(beats, stage, startSec, totalDuration);
        }
        return Math.min(startSec + stage, totalDuration);
      }
      case "seconds":
        return Math.min(startSec + STAGE_DURATIONS[stage - 1], totalDuration);
    }
  }

  async nextStage(): Promise<boolean> {
    if (this.currentStage >= 6) return false;
    this.currentStage = (this.currentStage + 1) as Stage;
    await this.playCurrentStage();
    return true;
  }

  async replay(): Promise<void> {
    await this.playCurrentStage();
  }

  async playFull(): Promise<void> {
    if (!this.activeProvider || !this.currentTrack) {
      throw new Error("No track loaded.");
    }
    const state = this.activeProvider.getState();
    await this.activeProvider.playClip(0, state.duration);
  }

  pause(): void {
    this.activeProvider?.pause();
  }

  stop(): void {
    this.unsubProvider?.();
    this.activeProvider?.stop();
    this.activeProvider = null;
    this.currentTrack = null;
  }

  getStage(): Stage {
    return this.currentStage;
  }

  getState(): PlaybackState {
    return this.activeProvider?.getState() ?? { status: "idle", currentTime: 0, duration: 0 };
  }

  getTrack(): GameTrack | null {
    return this.currentTrack;
  }

  getStageScore(): number {
    return Math.max(0, 7 - this.currentStage);
  }

  /** Get analysis info for display (number of detected onsets/beats, BPM, etc.) */
  getAnalysisInfo(): { onsetCount: number; beatCount: number; bpm: number } | null {
    if (!this.currentTrack) return null;
    return {
      onsetCount: this.currentTrack.onsets?.onsets.length ?? 0,
      beatCount: this.currentTrack.beatData?.beats.length ?? 0,
      bpm: this.currentTrack.beatData?.bpm ?? 0,
    };
  }

  onStateChange(listener: PlaybackListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private resolveStartSec(start: StartMode | undefined, safeMax: number): number {
    if (!start) return 0;
    switch (start.kind) {
      case "fixed":
        return Math.min(start.startSec, safeMax);
      case "random":
        return Math.random() * safeMax;
      case "random-range": {
        const min = Math.max(0, start.minSec);
        const max = Math.min(start.maxSec, safeMax);
        return min + Math.random() * (max - min);
      }
      case "random-avoid": {
        const maxAttempts = 20;
        for (let i = 0; i < maxAttempts; i++) {
          const candidate = Math.random() * safeMax;
          const tooClose = start.excludeSecs.some(
            (s) => Math.abs(candidate - s) < start.minDistance
          );
          if (!tooClose) return candidate;
        }
        return Math.random() * safeMax;
      }
    }
  }
}
